import {
  type EfsaSampnts,
  type Avdeling,
  type Matrices,
  type PlanSummary,
  type Products,
  type Region,
  type Seksjon,
  resultFilterOptions,
  type Substances,
  type Team,
  statusFilterOptions
} from '../../index'
import { getSampleFieldLabel } from '../../utils/sampleFieldDisplayUtils'
import { RJSFSchema } from '../form/rjsf'

type GetSampleFilterSchemaProps = {
  products: Products
  matrices: Matrices
  sampntTypes: EfsaSampnts
  substances: Substances
  plans?: PlanSummary[]
  regioner?: Region[]
  avdelinger?: Avdeling[]
  seksjoner?: Seksjon[]
  teams?: Team[]
  includePlanFilter?: boolean
  includeRegionFilter?: boolean
}

type SelectOption = { const: string; title: string }

const withEmptyOption = (options: SelectOption[]): SelectOption[] =>
  options.length > 0 ? options : [{ const: '', title: 'Ingen alternativer' }]

export const getSampleFilterSchema = ({
  products,
  matrices,
  sampntTypes,
  substances,
  plans = [],
  regioner = [],
  avdelinger = [],
  seksjoner = [],
  teams = [],
  includePlanFilter = false,
  includeRegionFilter = false
}: GetSampleFilterSchemaProps): RJSFSchema => ({
  type: 'object',
  properties: {
    status: {
      type: 'array',
      title: getSampleFieldLabel('status'),
      description:
        'Velg status for å filtrere prøver. Hvis ingen status er valgt, vises prøver for alle statuser.',
      default: [],
      minItems: 0,
      uniqueItems: true,
      items: {
        type: 'string',
        oneOf: statusFilterOptions
      }
    },
    result: {
      type: 'string',
      title: getSampleFieldLabel('result'),
      description:
        'Velg resultat for å filtrere prøver. Hvis ingen resultater er valgt, vises prøver for alle resultater.',
      oneOf: resultFilterOptions
    },
    productIds: {
      type: 'array',
      title: getSampleFieldLabel('productIds'),
      description:
        'Velg produkt for å filtrere prøver. Hvis ingen produkter er valgt, vises prøver for alle produkter.',
      default: [],
      minItems: 0,
      maxItems: products.length === 0 ? 0 : undefined,
      readOnly: products.length === 0,
      uniqueItems: true,
      items: {
        type: 'string',
        oneOf: withEmptyOption(
          products.map((product) => ({
            const: product.id.toString(),
            title: product.name
          }))
        )
      }
    },
    matrixIds: {
      type: 'array',
      title: getSampleFieldLabel('matrixIds'),
      default: [],
      minItems: 0,
      maxItems: matrices.length === 0 ? 0 : undefined,
      readOnly: matrices.length === 0,
      uniqueItems: true,
      items: {
        type: 'string',
        oneOf: withEmptyOption(
          matrices.map((matrix) => ({
            const: matrix.id.toString(),
            title: matrix.name
          }))
        )
      }
    },
    substanceIds: {
      type: 'array',
      title: getSampleFieldLabel('substanceIds'),
      description:
        'Velg analyser for å filtrere prøver. Hvis ingen analyser er valgt, vises prøver for alle analyser.',
      default: [],
      minItems: 0,
      maxItems: substances.length === 0 ? 0 : undefined,
      readOnly: substances.length === 0,
      uniqueItems: true,
      items: {
        type: 'string',
        oneOf: withEmptyOption(
          substances.map((substance) => ({
            const: substance.id.toString(),
            title: substance.name
          }))
        )
      }
    },
    fromDate: {
      type: 'string',
      format: 'date',
      title: getSampleFieldLabel('fromDate'),
      description:
        'Velg fra dato for å filtrere prøver etter uttaksdato fra denne datoen og fremover. Hvis ingen dato velges, vises prøver fra alle uttaksdatoer.'
    },
    toDate: {
      type: 'string',
      format: 'date',
      title: getSampleFieldLabel('toDate'),
      description:
        'Velg til dato for å filtrere prøver etter uttaksdato frem til denne datoen. Hvis ingen dato velges, vises prøver fra alle uttaksdatoer.'
    },
    sampntTypeIds: {
      type: 'array',
      title: getSampleFieldLabel('sampntTypeIds'),
      description:
        'Velg uttakssted for å filtrere prøver. Hvis ingen uttakssteder er valgt, vises prøver for alle uttakssteder.',
      default: [],
      minItems: 0,
      maxItems: sampntTypes.length === 0 ? 0 : undefined,
      readOnly: sampntTypes.length === 0,
      uniqueItems: true,
      items: {
        type: 'string',
        oneOf: withEmptyOption(
          sampntTypes.map((sampntType) => ({
            const: sampntType.id.toString(),
            title: sampntType.nameNo
          }))
        )
      }
    },
    producerSearch: {
      type: 'string',
      title: getSampleFieldLabel('producerSearch'),
      description:
        'Søk etter produsentnavn eller produsentnummer for å filtrere prøver på produsent.'
    },
    ...(includePlanFilter && {
      planIds: {
        type: 'array',
        title: getSampleFieldLabel('planIds'),
        default: [],
        minItems: 0,
        maxItems: plans.some((plan) => plan.parents?.length === 0)
          ? undefined
          : 0,
        readOnly: !plans.some((plan) => plan.parents?.length === 0),
        uniqueItems: true,
        items: {
          type: 'string',
          oneOf: withEmptyOption(
            plans
              .filter((plan) => plan.parents?.length === 0)
              .map((plan) => ({
                const: plan.id.toString(),
                title: plan.name ?? plan.id.toString()
              }))
          )
        }
      }
    }),
    ...(includeRegionFilter && {
      regionId: {
        type: 'string',
        title: getSampleFieldLabel('regionId'),
        oneOf: withEmptyOption(
          regioner.map((region) => ({
            const: region.id,
            title: region.navn
          }))
        )
      },
      avdelingId: {
        type: 'string',
        title: getSampleFieldLabel('avdelingId'),
        oneOf: withEmptyOption(
          avdelinger.map((avdeling) => ({
            const: avdeling.id,
            title: avdeling.navn
          }))
        )
      },
      seksjonId: {
        type: 'string',
        title: getSampleFieldLabel('seksjonId'),
        oneOf: withEmptyOption(
          seksjoner.map((seksjon) => ({
            const: seksjon.id,
            title: seksjon.navn
          }))
        )
      },
      teamId: {
        type: 'string',
        title: getSampleFieldLabel('teamId'),
        oneOf: withEmptyOption(
          teams.map((team) => ({
            const: team.id,
            title: team.name
          }))
        )
      }
    })
  }
})
